<template>
  <div class="container py-4">
    <div class="row justify-content-center"> 
      <div class="col-md-8 b-3">
        <div class="page-bar py-3">
          <div class="d-flex align-items-center">
            <BackButton class="btn btn-link p-0 pl-2 mr-2" fallback="/host">
              <i class="fas fa-chevron-left"></i>
            </BackButton>
            <div class="page-title flex-grow-1">Feedbacks</div> 
          </div>
        </div>
      </div>

      <div class="col-md-8">
        <div class="card shadow-sm">
          <div class="card-body p-0">
            <div class="container mt-3 mb-5"> 
              <!-- List -->
              <div class="card">
                <div class="card-body p-0">
                  <div v-if="loading" class="text-center p-4">
                    <div class="spinner-border text-primary" role="status">
                      <span class="sr-only">Loading...</span>
                    </div>
                  </div>

                  <div v-else-if="filteredFeedbacks.length === 0" class="text-center p-4">
                    <p class="text-muted">No Feedbacks found</p>
                  </div>

                  <div v-else>
                    <ul class="list-group">
                      <li
                        v-for="fb in filteredFeedbacks"
                        :key="fb.feedback_id"
                        class="guest"
                      >
                        <div class="flex-grow-1">
                          <div class="d-flex align-items-start justify-content-between">
                            <div class="min-w-0">
                              <div class="guest">
                                    <img
                                    class="avatar"
                                    :src="fb.user_img || placeholderAvatar"
                                    :alt="fb.name || fb.username || 'User'"
                                    />
                                    <div class="flex-grow-1">
                                        <p class="name mr-2 mb-1 text-truncate">{{ fb.name || fb.username }}</p>
                                        <p class="mb-1 text-truncate small text-muted">
                                            <span class="handle">@{{ fb.username }}</span>
                                            <span class="mx-1">•</span>
                                            <span>{{ formatDateTime(fb.created_date) }}</span>
                                        </p>
                                    </div>
                              </div>
                              <p class="mb-0" style="white-space:pre-wrap;">{{ fb.message }}</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <!-- /Items -->
                </div>
              </div>
              <!-- /List -->

            </div><!-- /container -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';
import { useSessionStore } from '@/stores/session';
import { formatDisplayDateTime } from '@/utils/date';
import BackButton from '@/components/common/BackButton.vue';

export default {
  name: 'CoHostList', // you can rename this to FeedbackList if you like
  components: { BackButton },
  setup() {
    const route = useRoute();
    const session = useSessionStore();

    // derive event id robustly
    const deriveEventId = (r) => {
      const cand = [
        r.params?.eventid, r.params?.eventId, r.params?.id,
        r.query?.eventid,  r.query?.eventId,  r.query?.id
      ].find(v => v != null && String(v).trim() !== '');
      if (cand) return String(cand);
      const last = (r.path || r.fullPath || '')
        .split('?')[0].split('/').filter(Boolean).pop();
      return last && /^\d+$/.test(last) ? last : null;
    };

    const eventId = ref(deriveEventId(route));
    watch(() => route.fullPath, () => { eventId.value = deriveEventId(route); });

    const loading = ref(true);
    const feedbacks = ref([]);
    const placeholderAvatar = 'https://dummyimage.com/200x200/222/fff.png?text=U';
    const searchQuery = ref(''); // keep for future filter UI

    // safe date formatter
    function formatDateTime(dtStr) {
      if (!dtStr) return ''
      const formatted = formatDisplayDateTime(dtStr, {
        locale: undefined,
        options: { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }
      })
      return formatted || dtStr
    }

    const filteredFeedbacks = computed(() => {
      const q = (searchQuery.value || '').trim().toLowerCase();
      if (!q) return feedbacks.value;
      return feedbacks.value.filter((f) => {
        return (
          String(f.message || '').toLowerCase().includes(q) ||
          String(f.name || '').toLowerCase().includes(q) ||
          String(f.username || '').toLowerCase().includes(q) ||
          String(f.phone || '').toLowerCase().includes(q)
        );
      });
    });

    // load feedbacks via feedbackList API
    async function loadFeedbacks() {
      loading.value = true;
      try {
        const fd = new FormData();
        if (eventId.value) fd.append('event_id', String(eventId.value));
        // some backends expect user id too (harmless if empty)
        fd.append('user_id', String(session.userId || ''));

        const res = await api.feedbackList(fd).catch((err) => {
          console.error('feedbackList API error', err);
          return null;
        });

        const payload = res?.data ?? res;
        const ok = payload?.status === 'ok' || payload?.status === true || res?.status === true;

        const list =
          (ok && Array.isArray(payload?.data) && payload.data) ||
          (ok && Array.isArray(payload?.list) && payload.list) ||
          [];

        // map server fields to local shape (defensive)
        feedbacks.value = list.map((it) => ({
          feedback_id: it.feedback_id || it.id || String(Math.random()).slice(2),
          id: it.id || null,
          created_date: it.created_date || it.date || it.created_at || '',
          message: it.message || it.msg || '',
          username: it.username || it.user || '',
          phone: it.phone || '',
          phone_code: it.phone_code || '',
          user_img: it.user_img || it.photo || '',
          name: it.name || it.fullname || '',
        }));
      } catch (err) {
        console.error('loadFeedbacks error', err);
        feedbacks.value = [];
      } finally {
        loading.value = false;
      }
    }

    onMounted(loadFeedbacks);

    return {
      // state
      loading,
      feedbacks,
      filteredFeedbacks,
      placeholderAvatar,
      // helpers
      formatDateTime,
      searchQuery,
      eventId,
      loadFeedbacks,
    };
  },
};
</script>

<style>
.guest {
  display: flex;
  align-items: center;
  padding: .8rem .5rem;
  border-bottom: 1px solid rgba(0,0,0,.06);
  transition: background-color 0.2s ease, border-left-color 0.2s ease;
}
.guest:last-child { border-bottom: 0; }

.avatar {
  width: 42px; height: 42px; border-radius: 50%;
  object-fit: cover; margin-right: .75rem;
  box-shadow: 0 1px 5px rgba(0,0,0,.1);
}
.guest .name { font-weight: 600; margin-bottom: 0; }
.guest .handle { color: #888; font-size: .85rem; }
.min-w-0 { min-width: 0; }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.section-title { font-weight: 700; }
</style>
